<template>
  <!-- ******* HEADER ******* -->
  <header>
    <nav class="new-navbar" :class="isMobileOpen ? 'nav-open' : ''">
      <div class="container">
        <div class="nav-flex">
          <div class="nav-hamburger d-lg-none">
            <a @click="toggleMobile">
              <font-awesome-icon :icon="isMobileOpen ? 'times' : 'bars'" />
            </a>
          </div>
          <div class="nav-logo">
            <NuxtLink class="navbar-brand" to="/">
              <span class="sr-only">AppSumo</span>
            </NuxtLink>
          </div>
          <div class="nav-search" :class="{ 'apply-top-row-margin' : isMobileOpen }">
            <div class="search-container desktop-search-container-inner">
              <SearchDropdownForm
                id="site-search"
                :options="options"
                slug="group"
              />
            </div>
          </div>
          <div class="nav-user">
            <div class="sell-on-appsumo">
              <a
                v-if="showPartnerPortal"
                href="/partners/overview/"
                class="partner"
              >
                <img
                  src="../../../static/images/svg/shopping-bag-confetti.svg"
                  class="d-lg-none"
                  alt="Partner portal banner"
                >
                <strong>Partner Portal</strong>
                <span class="d-lg-none">Manage your digital products</span>
              </a>
              <a
                v-else
                href="https://sell.appsumo.com"
                class="sell"
              >
                <img
                  src="../../../static/images/svg/light-bulb-confetti.svg"
                  class="d-lg-none"
                  alt="Sell on AppSumo banner"
                >
                <strong>Sell on AppSumo</strong>
                <span class="d-lg-none">Earn money selling digital products</span>
              </a>
            </div>
            <!-- User dropdown -->
            <div>
              <div v-if="currentUser" class="dropdown hover-drop">
                <a
                  id="user-link"
                  href="/account/profile/"
                >
                  <img
                    v-if="currentUser.avatar_small"
                    :src="currentUser.avatar_small"
                    alt="User avatar"
                    width="22"
                    height="22"
                  >
                  <font-awesome-icon v-else icon="user" />
                  <font-awesome-icon icon="chevron-down" />
                  <span>{{ currentUser.username }}</span>
                </a>
                <ul class="dropdown-menu">
                  <li>
                    <NuxtLink to="/account/">
                      Account overview
                    </NuxtLink>
                  </li>
                  <li>
                    <NuxtLink to="/account/profile/">
                      Profile
                    </NuxtLink>
                  </li>
                  <li>
                    <NuxtLink to="/account/products/">
                      Products
                    </NuxtLink>
                  </li>
                  <li v-if="showWishlistsLink">
                    <NuxtLink to="/account/wishlists/">
                      Wishlists - Beta
                    </NuxtLink>
                  </li>
                  <li v-if="currentUser.has_plus">
                    <NuxtLink to="/account/plus/">
                      Plus membership
                    </NuxtLink>
                  </li>
                  <li v-else>
                    <a href="/plus/">Get 10% off</a>
                  </li>
                  <li>
                    <NuxtLink to="/account/credits/">
                      Credits
                    </NuxtLink>
                  </li>
                  <li><a href="https://go.appsumo.com/become-appsumo-affiliate?ref=account_menu">Affiliate program</a></li>
                  <li>
                    <NuxtLink to="/account/email/">
                      Email settings
                    </NuxtLink>
                  </li>
                  <li class="log-out-link">
                    <a href="/accounts/logout/">Log out</a>
                  </li>
                </ul>
              </div>
              <a
                v-else
                id="log-in-link"
                href="/accounts/login/"
              >
                Log in
              </a>
            </div>
            <!-- User dropdown -->
          </div>
          <div class="nav-controls">
            <!-- Notifications -->
            <a
              v-if="showBeamerNotification"
              id="beamerButton"
              class="sumo-notification"
              href="#notifications"
            >
              <span class="sr-only">Notifications</span>
              <font-awesome-icon icon="bell" />
            </a>
            <!-- Notifications -->
            <!-- Sumo Cart -->
            <div class="sumo-cart" :product="$store.state.session.cart.count ? 'yes' : 'no'">
              <span
                v-if="$store.state.session.cart.count"
                class="cart-num"
              >
                {{ $store.state.session.cart.count }}
              </span>
              <a href="/cart/">
                <span class="sr-only">Cart</span>
                <font-awesome-icon icon="shopping-cart" />
              </a>
            </div>
            <!-- Sumo Cart -->
          </div>
          <div class="nav-secondary">
            <div class="nav-categories">
              <ul>
                <li
                  v-for="option in groups"
                  :key="option.value"
                  :class="{
                    'active': (
                      $route.query.group === option.value ||
                      group === option.value
                    )
                  }"
                >
                  <NuxtLink :to="`/${option.value}/`">
                    {{ option.name }}
                  </NuxtLink>
                </li>
                <li
                  v-if="showAsksumoNavItem"
                  class="flex-shrink-1 text-truncate"
                >
                  <a
                    href="/asksumo/?ref=askumo-navitem"
                    class="text-truncate"
                  >
                    <img
                      :src="CommentDotsGradient"
                      role="presentation"
                      alt=""
                      width="25"
                      class="mr-1"
                    >
                    AskSumo
                    <span v-if="!currentUser">
                      and get 10% off
                    </span>
                  </a>
                </li>
                <li v-if="isBlackFridayCampaign">
                  <a href="/collections/black-friday-2022/">
                    <BlackFridayIconNavBar class="mr-1" />
                    Black Friday deals
                  </a>
                </li>
              </ul>
            </div>
            <!--
            As long the AskSumo navItem is present we need to hide the pill between 992px and 1200px
            to avoid overlapping with the nav items, using the class `d-lg-none d-xl-block`
            see components/navigation/components/NavGroups.tsx for AskSumo nav item.
            -->
            <div
              class="nav-promo"
              :class="{'d-lg-none d-xl-block': showAsksumoNavItem }"
            >
              <NavBarPromoButton />
            </div>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>
<script>
import catchify from 'catchify';
import get from 'lodash/get';
import {
  BLACK_FRIDAY_SLUG,
  GETBEAMER_SEGMENT_TYPE,
  ASKUMO_CLEANUP_EXPERIMENT_NAME,
} from '@/constants/global';
import { DEALS_URL } from '@/constants/api';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import NavBarPromoButton from './NavBarPromoButton.vue';
import SearchDropdownForm from '../search/SearchDropdownForm.vue';
import BlackFridayIconNavBar from '~/static/campaigns/bf-nav-bar-icon.svg?inline';
import CampaignsMixin from '~/mixins/campaigns';
import CommentDotsGradient from '~/static/comment-dots-gradient.svg';

export default {
  components: {
    FontAwesomeIcon,
    NavBarPromoButton,
    SearchDropdownForm,
    BlackFridayIconNavBar,
  },
  mixins: [CampaignsMixin],
  props: {
    group: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      options: [],
      showBeamerNotification: false,
      isMobileOpen: false,
      CommentDotsGradient,
    };
  },
  async fetch() {
    await this.fetchSuggestions();
    await this.$store.dispatch('experiments/enrollByName', ASKUMO_CLEANUP_EXPERIMENT_NAME);

    if (this.$config.getBeamerProductId) {
      await this.initGetBeamerNotification();
    }
  },
  head() {
    return {
      script: [
        {
          skip: !this.showBeamerNotification,
          hid: 'getbeamer-productid',
          innerHTML: `var beamer_config = ${JSON.stringify(this.getBeamerConfig)};`,
          body: true,
        },
        {
          skip: !this.showBeamerNotification,
          hid: 'getbeamer-script',
          src: 'https://app.getbeamer.com/js/beamer-embed.js',
          defer: true,
          body: true,
        },
      ],
      __dangerouslyDisableSanitizersByTagID: {
        'getbeamer-productid': ['innerHTML'],
      },
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.session.user;
    },
    isBlackFridayCampaign() {
      return this.isCampaignActive(BLACK_FRIDAY_SLUG);
    },
    getBeamerConfig() {
      if (!this.showBeamerNotification) {
        return {};
      }

      const config = {
        product_id: this.$config.getBeamerProductId,
        selector: '#beamerButton',
        top: -10,
        right: -9,
        user_type: GETBEAMER_SEGMENT_TYPE.ANONYMOUS, // an: for getBeamer segments report
        filter: GETBEAMER_SEGMENT_TYPE.ANONYMOUS, // an: for getBeamer post detail report
      };

      const { user } = this.$store.state.session;

      if (user) {
        const userSegmentType = user.has_plus
          ? GETBEAMER_SEGMENT_TYPE.PLUS
          : GETBEAMER_SEGMENT_TYPE.NON_PLUS;

        config.user_email = user.email;
        config.user_firstname = user.first_name;
        config.user_lastname = user.last_name;
        config.user_type = userSegmentType;
        config.filter = userSegmentType;
      }

      return config;
    },
    groups() {
      const group = this.$store.state.browse.attributes.find((a) => a.slug === 'group');

      return group?.type_options?.options || [];
    },
    showPartnerPortal() {
      return get(this, ['$store', 'state', 'session', 'user', 'has_partner_portal']);
    },
    wishlistExperiment() {
      return this.$store.getters['wishlists/wishlistExperiment'];
    },
    showWishlistsLink() {
      return this.wishlistExperiment === 'enabled';
    },
    showAsksumoNavItem() {
      return this.$store.getters['experiments/getExperimentVariant'](
        ASKUMO_CLEANUP_EXPERIMENT_NAME,
      ) === 'enabled';
    },
  },
  watch: {
    $route() { // on page change
      this.isMobileOpen = false;
      this.$store.dispatch('setShowBodyOverlay', false);
    },
    // TODO:: Need to clean this up in the future.
    '$store.state.autocompleteQuery': function () { // eslint-disable-line func-names
      this.fetchSuggestions();
    },
    '$store.state.autocompleteProductType': function () { // eslint-disable-line func-names
      this.fetchSuggestions();
    },
    '$store.state.autocompleteGroup': function () { // eslint-disable-line func-names
      this.fetchSuggestions();
    },
  },
  methods: {
    toggleMobile() {
      this.isMobileOpen = document.querySelector('body').classList.toggle('overflow-hidden');
      this.$store.dispatch('setShowBodyOverlay', this.isMobileOpen);
    },
    async initGetBeamerNotification() {
      const { user } = this.$store.state.session;

      if (user) {
        this.showBeamerNotification = true;
      } else if (this.$route.query?.notify_anonymous === 'true') {
        this.showBeamerNotification = true;
      }
    },
    async fetchSuggestions() {
      const query = this.$store.state.autocompleteQuery;
      const productType = this.$store.state.autocompleteProductType;
      const group = this.$store.state.autocompleteGroup;

      let apiUrl = `${DEALS_URL}/?status=current&sort=recommended&toptrending=1&per_page=5`;

      if (query && query.length) {
        const { categories } = this.$store.state.browse;
        const lowerQuery = query.toLowerCase();
        const category = categories.find(
          (c) => c.display.toLowerCase().localeCompare(lowerQuery) === 0,
        );

        if (category) {
          apiUrl = `${DEALS_URL}/?status=all&sort=recommended&product_tags[all]=${category.value}&per_page=5&autocomplete=1`;
        } else {
          apiUrl = `${DEALS_URL}/?status=all&sort=recommended&query=${query}&per_page=5&autocomplete=1`;
        }
      }

      if (productType) {
        apiUrl += `&attribute[product_type]=${productType}`;
      }

      if (group) {
        apiUrl += `&attribute[group]=${group}`;
      }

      const [error, response] = await catchify(
        this.$cachios.get(apiUrl, { cdn: true }),
      );

      if (error || !(response && response.data && response.data.deals)) return;

      this.options = response.data.deals || [];
    },
  },
};
</script>
<style lang="scss">
@import '~/styles/components/navbar';

.navbar.fixed-top .container {
  max-width: 2000px;
  margin: auto;
  padding: 0 25px;
}

.nav-item {
  border: 0 !important;
  border-color: transparent !important;
}

/* Handle overlay for when Search is active */

#sumo-main-overlay {
  z-index: 1050 !important;  /* Needs to be > 1020 due to PDP Deal */
  position: fixed !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: none;
}

#sumo-main-overlay.full {
  z-index: 9999 !important;  /* Ensures we also cover the page header */
}

#sumo-main-overlay.active {
  display: inline !important;
}
</style>
